import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FlaskConical, FileCheck2, Truck, BadgeCheck, Sparkles } from "lucide-react";
import api from "../lib/apiClient";
import ProductCard from "../components/ProductCard";
import VialShowcase from "../components/VialShowcase";
import { Reveal } from "../components/Reveal";

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.slice(0, 8))).catch(() => {});
  }, []);

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center py-14 lg:py-24">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 eyebrow pill border border-[rgba(197,155,39,0.4)] px-4 py-1.5 bg-white/70 backdrop-blur-sm">
              <Sparkles className="w-3.5 h-3.5 text-champagne" /> Research Materials
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mt-6">
              Research-grade compounds,<br /><span className="gold-text">meticulously documented.</span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground mt-6 max-w-md leading-relaxed">
              Ethos Research Labs supplies research materials and reconstitution solutions for in-vitro laboratory research — each batch backed by third-party certificates of analysis.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/shop" data-testid="hero-shop-button" className="h-13 px-7 py-3.5 rounded-full btn-gold font-semibold flex items-center gap-2 group">
                Shop Catalog <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/coa" data-testid="hero-coa-button" className="h-13 px-7 py-3.5 rounded-full btn-outline-gold font-semibold flex items-center">
                View COAs
              </Link>
            </div>
          </div>
          <div className="relative animate-fade-up w-full" style={{ animationDelay: "120ms" }}>
            {/* ethereal halo behind the vial */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[115%] h-[115%] rounded-full glow-pulse pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(230,198,101,0.35) 0%, rgba(255,232,235,0.22) 45%, transparent 70%)" }} aria-hidden="true" />
            <div className="relative rounded-[2rem] overflow-hidden w-full max-w-[560px] lg:ml-auto shadow-2xl ring-1 ring-black/5">
              <img src="/assets/ethos-hero-vials.png" alt="Ethos Research Labs E-RT, MOTS-C and ETHOS-TESA research vials"
                className="w-full h-auto object-cover" />
            </div>
            {/* floating verification chip */}
            <div className="absolute -bottom-4 -left-4 sm:-left-6 flex items-center gap-2.5 rounded-2xl card-glass px-4 py-3" data-testid="hero-purity-chip">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(197,155,39,0.14)" }}>
                <BadgeCheck className="w-5 h-5 text-champagne" />
              </div>
              <div className="leading-tight">
                <p className="font-display text-2xl text-foreground">≥99% purity</p>
                <p className="text-[11px] text-muted-foreground">Third-party verified</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: FileCheck2, t: "Third-party tested", s: "Lab certificates available" },
            { icon: FlaskConical, t: "Research use only", s: "In-vitro laboratory materials" },
            { icon: Truck, t: "Fast U.S. shipping", s: "Free shipping over $200" },
            { icon: ShieldCheck, t: "Native checkout", s: "Secure Attestly payments" },
          ].map((b, i) => (
            <div key={i} className="card-glass rounded-2xl p-5 flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(197,155,39,0.14)" }}>
                <b.icon className="w-5 h-5 text-champagne" />
              </div>
              <div>
                <p className="font-display text-xl leading-none mt-0.5">{b.t}</p>
                <p className="text-xs text-muted-foreground mt-1.5">{b.s}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Rotating vials showcase */}
      <VialShowcase products={products} />

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="eyebrow">Featured</span>
            <h2 className="font-display text-4xl sm:text-5xl mt-2 leading-tight">Popular research materials</h2>
          </div>
          <Link to="/shop" className="hidden sm:flex items-center gap-1.5 font-semibold text-champagne hover:gap-2.5 transition-all">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* COA band */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
        <div className="relative overflow-hidden rounded-3xl bg-[#1a1815] text-white p-10 sm:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full opacity-30 blur-3xl" style={{ background: "#c59b27" }} aria-hidden="true" />
          <div className="max-w-xl relative">
            <span className="eyebrow gold-text">Certificates of Analysis</span>
            <h2 className="font-display text-4xl sm:text-5xl mt-3 leading-tight" data-testid="home-coa-heading">Research documentation, in one place.</h2>
            <p className="text-white/65 mt-4 leading-relaxed" data-testid="home-coa-status">
              Independent laboratory certificates from Freedom Diagnostics—listed by product with measured purity by HPLC-UV / LC-MS. Open or download each PDF from the directory.
            </p>
          </div>
          <Link to="/coa" className="relative h-13 px-7 py-3.5 rounded-full btn-gold font-semibold flex items-center gap-2 shrink-0">
            Browse COAs <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Join the community */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24 mb-8">
        <Reveal>
          <div
            className="relative overflow-hidden rounded-3xl p-10 sm:p-14 text-center"
            style={{ background: "linear-gradient(135deg, #4752c4 0%, #5865F2 55%, #7c3aed 100%)" }}
            data-testid="home-community-section"
          >
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-25 blur-3xl" style={{ background: "#ffffff" }} aria-hidden="true" />
            <div className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ background: "#c59b27" }} aria-hidden="true" />
            <div className="relative">
              <span className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-sm mb-6" aria-hidden="true">
                <svg viewBox="0 0 24 24" className="w-9 h-9 fill-white" aria-hidden="true">
                  <path d="M20.317 4.369a19.79 19.79 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.249a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.036A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.1 13.1 0 01-1.872-.892.077.077 0 01-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.009c.12.099.246.198.373.292a.077.077 0 01-.006.127 12.3 12.3 0 01-1.873.891.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.84 19.84 0 006.002-3.03.077.077 0 00.032-.056c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.028zM8.02 15.33c-1.182 0-2.157-1.086-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.332-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.086-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.332-.946 2.418-2.157 2.418z" />
                </svg>
              </span>
              <h2 className="font-display text-4xl sm:text-5xl text-white leading-tight" data-testid="home-community-heading">
                Join the research community
              </h2>
              <p className="text-white/80 mt-4 max-w-xl mx-auto leading-relaxed">
                Connect with fellow researchers on our Discord — get restock alerts, new-compound announcements, COA drops, and direct answers from the Ethos team.
              </p>
              <a
                href="https://discord.gg/fDpJ3qGDwU"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-community-discord-button"
                className="inline-flex items-center gap-2 mt-8 h-13 px-8 py-3.5 rounded-full bg-white text-[#4752c4] font-semibold shadow-lg hover:-translate-y-0.5 transition-transform"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
                  <path d="M20.317 4.369a19.79 19.79 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.249a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.036A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.1 13.1 0 01-1.872-.892.077.077 0 01-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.009c.12.099.246.198.373.292a.077.077 0 01-.006.127 12.3 12.3 0 01-1.873.891.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.84 19.84 0 006.002-3.03.077.077 0 00.032-.056c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.028zM8.02 15.33c-1.182 0-2.157-1.086-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.332-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.086-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.332-.946 2.418-2.157 2.418z" />
                </svg>
                Join our Discord
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
